/**
 * Hộp thư trong app — ba móc cho chuông ở thanh trên và huy hiệu "tiền về".
 *
 * MỘT cửa `/notifications/unread-count` nuôi CẢ HAI chỗ: chấm số trên chuông và
 * huy hiệu trên bảng tiền của đơn. Hai cửa riêng là hai con số lệch nhau trên
 * cùng một màn hình — người dùng thấy chuông báo 3 mà đơn không sáng, hoặc
 * ngược lại, rồi không tin cái nào nữa.
 */

import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import type { components } from "@/lib/api/generated/schema";
import { serverErrorMessage } from "@/lib/api/error-message";
import { usePermission } from "@/lib/authv2/permissions";
import { ACTION } from "@/lib/auth/actions";

export type StaffNotification = components["schemas"]["StaffNotification"];
export type UnreadCount = components["schemas"]["StaffNotificationUnreadCount"];
export type MarkReadInput = components["schemas"]["StaffNotificationMarkRead"];
export type NotificationGroup = components["schemas"]["StaffNotificationGroup"];
export type NotificationFacet = components["schemas"]["StaffNotificationFacet"];
export type NotificationPage = components["schemas"]["StaffNotificationPage"];
export type NotificationMatch = components["schemas"]["StaffNotificationMatch"];
export type NotificationStatus = NonNullable<NotificationMatch["status"]>;
export type NotificationSort = "newest" | "oldest" | "unread_first";

/** Tham số trang "Thông báo của tôi" — đúng tên tham số máy chủ đọc. */
export type NotificationPageParams = NotificationMatch & {
  sort?: NotificationSort;
  limit: number;
  offset: number;
};

// Mọi khoá nằm dưới ["notifications", …]: đánh dấu ở đâu cũng làm mới chuông,
// huy hiệu 💸 và trang cùng lúc qua một lần invalidate `all`.
export const notificationKeys = {
  all: ["notifications"] as const,
  unread: ["notifications", "unread"] as const,
  list: ["notifications", "list"] as const,
  page: (p: NotificationPageParams) => ["notifications", "page", p] as const,
};

export function useUnreadCount() {
  // Vai không đọc được thư (Chỉ xem, vai đã khoá) thì không hỏi: chuông đã ẩn
  // theo đúng quyền này ở Header, nhưng chi tiết đơn và chat khách gọi móc này
  // thẳng nên vẫn ăn 403 mỗi 60 giây (kiểm thử 18/10/2026, F-011).
  const canRead = usePermission(ACTION.NotificationRead);
  return useQuery({
    queryKey: notificationKeys.unread,
    enabled: canRead,
    queryFn: async (): Promise<UnreadCount> => {
      const { data, error } = await api.GET("/notifications/unread-count");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đọc được số thư chưa xem"));
      return data;
    },
    // 60 giây: đủ nhanh để tin "tiền về" tới trong lúc khách còn đứng quầy, đủ
    // chậm để mấy chục người ngồi cùng lúc không nện máy chủ. KHÔNG dùng thời
    // gian thực — một chấm số không đáng mở một kết nối treo.
    refetchInterval: 60_000,
    // Vẫn hỏi khi tab ERP đang ẩn (người dùng ở tab Zalo, Messenger…): tiếng
    // "ting ting" là để báo lúc họ KHÔNG nhìn ERP — tab ẩn mà ngừng hỏi thì tin
    // tới cũng im (CEO chốt 1a, 21/10/2026).
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    // Hộp thư rỗng là chuyện thường (nhân sự mới, hoặc chưa có hồ sơ) — đừng
    // để màn hình nhấp nháy chấm số khi mạng chập chờn.
    retry: 1,
  });
}

/**
 * Chỉ gọi khi chuông thật sự mở: danh sách không cần nạp sẵn cho mọi trang.
 * 50 dòng = trần của máy chủ (soát 17/10/2026: 20 dòng thì người có hơn 20 tin
 * chưa đọc bấm hết các dòng đang thấy mà số đỏ vẫn còn).
 */
export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: notificationKeys.list,
    enabled,
    queryFn: async (): Promise<StaffNotification[]> => {
      const { data, error } = await api.GET("/notifications", {
        params: { query: { limit: 50 } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được hộp thư"));
      return data.items;
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MarkReadInput) => {
      const { data, error } = await api.POST("/notifications/read", { body: input });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đánh dấu đã đọc được"));
      return data;
    },
    // Làm mới CẢ hai khoá: chấm số trên chuông và huy hiệu trên đơn cùng đọc từ
    // đây, bỏ sót một cái là màn hình tự mâu thuẫn với chính nó.
    onSuccess: () => void qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/**
 * Một trang hộp thư CỦA CHÍNH người đăng nhập (trang "Thông báo của tôi").
 * Giữ trang cũ trong lúc tải trang mới để bảng không nháy trắng khi lật trang.
 */
export function useNotificationPage(params: NotificationPageParams) {
  return useQuery({
    queryKey: notificationKeys.page(params),
    queryFn: async (): Promise<NotificationPage> => {
      const { data, error } = await api.GET("/notifications/page", {
        params: { query: params },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được thông báo"));
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** Đánh dấu CHƯA đọc lại — số đỏ chuông và huy hiệu 💸 sáng lại theo. */
export function useMarkUnread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await api.POST("/notifications/unread", { body: { ids } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đánh dấu chưa đọc được"));
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/**
 * Đơn nào đang có tin tiền-về chưa đọc của người này.
 *
 * Trả `Set` chứ không trả mảng: nơi gọi là danh sách đơn, hỏi hàng chục lần mỗi
 * lượt vẽ — `includes` trên mảng là quét lại từ đầu mỗi lần hỏi.
 */
export function useMoneyInOrderIds(): Set<string> {
  const q = useUnreadCount();
  return new Set(q.data?.orderIds ?? []);
}

/**
 * Đơn đang mở có tin "tiền về" chưa đọc không — và MỞ ĐƠN LÀ ĐÃ ĐỌC (CEO chốt
 * 3a, 17/10/2026). Trước đó chỉ bấm dòng trong chuông mới tắt: sale mở đơn từ
 * danh sách, thấy "💸 Tiền về", mà chuông vẫn đỏ mãi.
 *
 * Trả `true` suốt lượt xem đơn này kể cả sau khi máy chủ đã đánh dấu đọc: nhãn
 * trên nút "Thu tiền" và thẻ mở sẵn "Tra cứu" của hộp thu tiền vẫn dựa vào nó,
 * tắt ngay giữa lúc người ta đang nhìn là đổi màn dưới tay họ.
 */
export function useMoneyInOnOpen(orderId: string | undefined): boolean {
  const moneyIn = useMoneyInOrderIds();
  const { mutate } = useMarkRead();
  const [seenFor, setSeenFor] = useState<string | null>(null);
  const has = !!orderId && moneyIn.has(orderId);
  if (has && orderId && seenFor !== orderId) setSeenFor(orderId);
  useEffect(() => {
    if (!has || !orderId) return;
    // Đánh dấu hụt thì tin vẫn còn ở chuông — không chặn việc xem đơn.
    mutate({ orderId });
  }, [has, orderId, mutate]);
  return has || (!!orderId && seenFor === orderId);
}

/**
 * Mở một màn là tắt mọi tin trỏ ĐÚNG màn đó (CEO chốt 3a, 17/10/2026) — vd mở
 * khung chat của khách thì tin "… vừa nhắn" của khung ấy tính đã đọc, không phải
 * quay lại chuông bấm lần nữa. Chỉ gọi máy chủ khi hộp thư còn tin chưa đọc.
 */
export function useMarkLinkReadOnOpen(link: string | null): void {
  const unread = useUnreadCount();
  const { mutate } = useMarkRead();
  const hasUnread = (unread.data?.total ?? 0) > 0;
  useEffect(() => {
    if (link && hasUnread) mutate({ link });
  }, [link, hasUnread, mutate]);
}
